import { apiFetch } from "../api"
import { consumeChatSSE, ChatStreamCallbacks } from "../chat/sse"

export type {
    OrderingPropertyMeta,
    OrderingPropertiesForMarketResponse,
    OrderingPropertyType,
    OrderingPropertyValueHint,
} from "./orderingPropertyTypes"
export {
    getOrderingPropertiesForMarket,
    normalizeOrderingPropertiesResponse,
} from "./orderingPropertiesApi"
export {
    buildRankingPromptSnippet,
    formatPropertyTypeLabel,
    formatValueHintSummary,
    getPropertyDisplayLabel,
    isEnumHint,
    type NumericFilterOp,
    type DateFilterOp,
    type RankingPromptInsertOptions,
} from "./rankingPropertyPrompts"

/** One ranking row from GET /chat/list_rankings_per_market */
export interface RankingPerMarketItem {
    market: string
    id: string
    rankingText: string
    cypherQuery: string
    createdAt: string
    updatedAt: string
    createdBy: string
    lastEditedBy: string
    lastEditedByName: string
}

export interface ListRankingsPerMarketResponse {
    rankings: RankingPerMarketItem[]
}

/**
 * GET /chat/list_rankings_per_market — any authenticated user.
 * @param market optional filter (e.g. FUNDOS)
 */
export const listRankingsPerMarket = async (market?: string): Promise<ListRankingsPerMarketResponse> => {
    const qs = market ? `?market=${encodeURIComponent(market)}` : ""
    const raw = (await apiFetch(`/chat/list_rankings_per_market${qs}`)) as Record<string, unknown>
    const list = Array.isArray(raw.rankings) ? raw.rankings : []
    const rankings: RankingPerMarketItem[] = list
        .map((row) => normalizeRankingRow(row as Record<string, unknown>))
        .filter((r): r is RankingPerMarketItem => r !== null)
    return { rankings }
}

function normalizeRankingRow(row: Record<string, unknown>): RankingPerMarketItem | null {
    const market = String(row.market ?? "").trim()
    if (!market) return null
    const rankingText = String(row.rankingText ?? row.ranking_text ?? "").trim()
    const cypherQuery = String(row.cypherQuery ?? row.cypher_query ?? "").trim()
    return {
        market,
        id: String(row.id ?? ""),
        rankingText,
        cypherQuery,
        createdAt: String(row.createdAt ?? row.created_at ?? ""),
        updatedAt: String(row.updatedAt ?? row.updated_at ?? ""),
        createdBy: String(row.createdBy ?? row.created_by ?? ""),
        lastEditedBy: String(row.lastEditedBy ?? row.last_edited_by ?? ""),
        lastEditedByName: String(row.lastEditedByName ?? row.last_edited_by_name ?? ""),
    }
}

export interface ClarificationOption {
    label: string
    value: string
    description?: string
}

export interface ClarificationQuestion {
    header: string
    question: string
    options: ClarificationOption[]
    multi_select?: boolean
    allow_freeform?: boolean
}

export interface ClarificationAnswer {
    header: string
    answer: string
}

export interface SetSortingProductsResponse {
    response_json: string
    natural_language_response: string
    cypher_query?: string
    next_step?: number
    ranking_text?: string
    clarification_round?: number
    clarification?: {
        round: number
        questions: ClarificationQuestion[]
    }
}

export interface SetSortingProductsPayload {
    market: string
    ranking_text?: string
    step?: number
    user_reply?: string
    cypher_query?: string
    /** Explicit confirm/cancel from UI buttons — skips LLM classification. */
    action?: "confirm" | "cancel"
    clarification_round?: number
    clarification_answers?: ClarificationAnswer[]
}

export const RANKING_IN_USE_STORAGE_KEY = "rankingInUse"

export const setSortingProducts = async (
    payload: SetSortingProductsPayload,
    callbacks: ChatStreamCallbacks = {}
): Promise<SetSortingProductsResponse> => {
    const body: Record<string, unknown> = { market: payload.market }
    if (payload.step !== undefined) body.step = payload.step
    if (payload.ranking_text !== undefined) body.ranking_text = payload.ranking_text
    if (payload.user_reply !== undefined) body.user_reply = payload.user_reply
    if (payload.cypher_query !== undefined) body.cypher_query = payload.cypher_query
    if (payload.action !== undefined) body.action = payload.action
    if (payload.clarification_round !== undefined) body.clarification_round = payload.clarification_round
    if (payload.clarification_answers !== undefined) body.clarification_answers = payload.clarification_answers

    const data = await consumeChatSSE(
        "/chat/set_sorting_products",
        {
            method: "POST",
            body: JSON.stringify(body),
        },
        {
            onStatus: callbacks.onStatus,
            onToken: callbacks.onToken,
        }
    )

    const response = data as unknown as SetSortingProductsResponse

    if (response?.next_step === 3 && response.cypher_query && typeof localStorage !== "undefined") {
        try {
            localStorage.setItem(RANKING_IN_USE_STORAGE_KEY, response.cypher_query)
        } catch {
            // ignore
        }
    }

    return response
}
